import { useMemo, useState } from "react";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { dateUtility } from "../../../../../common/infrastructure/utilities/dateUtility";
import {
  AmountTone,
  formatSignedPrice,
  formatSignedEurValue,
  getAmountTone,
} from "../../../../../common/utilities/currencyUtility";
import { IUserDepositHistoryModel } from "../models/userDepositHistoryItemModel";
import { DepositTransactionMode } from "../models/userDepositTransactionFormModel";

interface IUseUserDepositDialogProps {
  userId?: number;
  visible: boolean;
}

export interface IUserDepositHistoryRow {
  id: number;
  amountText: string;
  amountTone: AmountTone;
  dateText: string;
  createdByName: string;
  comment: string;
  // Planner event of a class-payment row; null rows render non-clickable.
  eventId: number | null;
}

export const useUserDepositDialog = ({
  userId,
  visible,
}: IUseUserDepositDialogProps) => {
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [transactionMode, setTransactionMode] =
    useState<DepositTransactionMode>("topUp");

  const { data, isLoading } = useApiQuery<IUserDepositHistoryModel>({
    queryKey: [endpointsMetadata.deposit.getHistory, String(userId)],
    endpoint: `${endpointsMetadata.deposit.getHistory}?userId=${userId}`,
    enabled: visible && !!userId,
  });

  const balance = data?.balance ?? 0;
  const balanceText = formatSignedEurValue(balance);
  const balanceTone = getAmountTone(balance);

  // The server already returns the history newest-first; the sort here is a
  // defensive guarantee for the UI contract.
  const rows: IUserDepositHistoryRow[] = useMemo(() => {
    const items = data?.items ?? [];
    return items
      .slice()
      .sort(
        (a, b) =>
          new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime(),
      )
      .map((item) => ({
        id: item.id,
        // Each row renders in its own currency (null currencyId = EUR); the
        // dialog's balance itself stays a normalized EUR number.
        amountText: formatSignedPrice(item.amount, item.currencyId),
        amountTone: getAmountTone(item.amount),
        dateText: dateUtility.formatDate(item.createdOn),
        createdByName: item.createdByName,
        comment: item.comment,
        eventId: item.eventId ?? null,
      }));
  }, [data]);

  const openTopUpDialog = () => {
    setTransactionMode("topUp");
    setShowTransactionDialog(true);
  };

  const openWithdrawDialog = () => {
    setTransactionMode("withdraw");
    setShowTransactionDialog(true);
  };

  const closeTransactionDialog = () => {
    setShowTransactionDialog(false);
  };

  return {
    balance,
    balanceText,
    balanceTone,
    rows,
    isLoading,
    showTransactionDialog,
    transactionMode,
    openTopUpDialog,
    openWithdrawDialog,
    closeTransactionDialog,
  };
};
